import { WidgetSizeExplicit, WidgetSizeImplicit, WidgetStyle, WidgetTextStyle } from '../widget/common';
import TextTransform from '../enum/TextTransform';
import TextAlignment from '../enum/TextAlignment';
import animationTypeClassFilter from '../filter/animationTypeClass';
import AnimationType from '../enum/AnimationType';
import Orientation from '../enum/Orientation';
import ColorPreset from '../enum/ColorPreset';
import { fonts } from '../fonts';
import Font from '../enum/Font';

export function resolveWidgetStyle(style: WidgetStyle, showStyle, dimmed: boolean) {

    let result: any = {};

    if(dimmed){
        result.opacity = 0.2;
    }

    if(!style){
        return result;
    }

    if(style.borderSize){
        result['border'] = `${style.borderSize}px solid`;
    }

    if(style.borderColor){
        result['border-color'] = resolveColor(style.borderColor, showStyle);
    }

    if(style.opacity !== null && style.opacity != undefined && !dimmed){
        result['opacity'] = style.opacity / 100;
    }

    if(style.rotation !== null && style.rotation != undefined){
        result['transform'] = `rotate(${style.rotation}deg)`;
    }

    if(style.padding !== null && style.padding != undefined){
        result['padding'] = `${style.padding}px`;
    }

    if(style.borderRadius){
        result['border-radius'] = `${style.borderRadius}px`;
    }

    if(style.shadowColor){
        result['box-shadow'] = `${style.shadowOffsetX || 0}px ${style.shadowOffsetY || 0}px ${style.shadowBlur || 0}px ${resolveColor(style.shadowColor, showStyle) || ''}`;
    }

    return result;
}

export function resolveTextStyle(style: WidgetTextStyle, showStyle) {

    if(!style){
        return {};
    }

    let result = {};

    if(style.alignment){

        let alignment = null;

        switch(style.alignment){

            case TextAlignment.LEFT:
                alignment = 'left';
                break;

            case TextAlignment.CENTER:
                alignment = 'center';
                break;

            case TextAlignment.RIGHT:
                alignment = 'right';
                break;

            case TextAlignment.JUSTIFY:
                alignment = 'justify';
                break;
        }

        result['text-align'] = alignment;
    }

    if(style.bold){
        result['font-weight'] = 'bold';
    }

    if(style.borderColor){

        const borderSize = style.borderSize || 0;

        result['text-stroke-width'] = `${borderSize}px`;
        result['text-stroke-color'] = resolveColor(style.borderColor, showStyle);
    }

    if(style.shadowColor){
        result['text-shadow'] = `${style.shadowOffsetX || 0}px ${style.shadowOffsetY || 0}px ${style.shadowBlur || 0}px ${resolveColor(style.shadowColor, showStyle) || ''}`;
    }

    if (style.customFont)  {
        result['font-family'] = style.customFont;
    } else if(style.font){
        const getFontId = (font: Font) => {
            switch(style.font) {
                case Font.PRIMARY:
                    if (showStyle.fontPrimary === 'CUSTOM_FONT') {
                        return showStyle.fontPrimaryCustom;
                    }
                    return showStyle.fontPrimary;
                    break;
                case Font.SECONDARY:
                    if (showStyle.fontSecondary === 'CUSTOM_FONT') {
                        return showStyle.fontSecondaryCustom;
                    }
                    return showStyle.fontSecondary;
                    break;
                default:
                    return font;
                    break;
            }
        }

        const fontId = getFontId(style.font);
        const predefinedFont = fonts.find(f => f.id === fontId);
        if (predefinedFont) {
            result['font-family'] = predefinedFont.family;
        } else {
            result['font-family'] = fontId;
        }
    }

    if(style.color){
        result['color'] = resolveColor(style.color, showStyle);
    }

    if(style.italic){
        result['font-style'] = 'italic';
    }

    if(style.lineSpacing){
        result['line-height'] = style.lineSpacing;
    }

    if(style.characterSpacing) {
        result['letter-spacing'] = `${style.characterSpacing}px`;
    }

    if(style.size){
        result['font-size'] = `${style.size}%`;
    }

    if(style.transform){
        result['text-transform'] = style.transform === TextTransform.LOWERCASE ? 'lowercase' : (
            style.transform === TextTransform.UPPERCASE ? 'uppercase' : 'capitalize'
        );
    }

    if(style.underlined){
        result['text-decoration'] = 'underline';
    }

    return result;
}

export function resolveColor(value, showStyle){

    if(value && value.charAt(0) === '$'){

        if(!showStyle){
            return null;
        }

        if(value === ColorPreset.PRIMARY){
            return showStyle.colorPrimary ? '#' + showStyle.colorPrimary : null;
        }
        else if(value === ColorPreset.SECONDARY){
            return showStyle.colorSecondary ? '#' + showStyle.colorSecondary : null;
        }
        else if(value === ColorPreset.TERTIARY){
            return showStyle.colorTertiary ? '#' + showStyle.colorTertiary : null;
        }
    }

    if(value && value.charAt(0) !== '#'){
        return '#' + value
    }
    else {
        return value
    }
}

export function generateCustomAnimationCss(widget, animation): string {

    const base = getWidgetAnimationIdentifier(animation);

    const posA = {
        left: animation.positionStartX * 100,
        top: animation.positionStartY * 100,
        scale: animation.scaleStart || 1
    };

    const posB = {
        left: widget.positionX * 100,
        top: widget.positionY * 100
    }

    return `
    
        @keyframes ${base}In {
            from {
                left: ${posA.left}%;
                top: ${posA.top}%;
                transform: scale(${posA.scale});
            }
            to {
                left: ${posB.left}%;
                top: ${posB.top}%;
                transform: scale(1);
            }
        }
        
        @keyframes ${base}Out {
            from {
                left: ${posB.left}%;
                top: ${posB.top}%;
                transform: scale(1);
            }
            to {
                left: ${posA.left}%;
                top: ${posA.top}%;
                transform: scale(${posA.scale});
            }
        }
        
        @keyframes ${base}Loop {
            0% {
                left: ${posB.left}%;
                top: ${posB.top}%;
                transform: scale(1)
            }
            50% {
                left: ${posA.left}%;
                top: ${posA.top}%;
                transform: scale(${posA.scale});
            }
            100% {
                left: ${posB.left}%;
                top: ${posB.top}%;
                transform: scale(1)
            }
        }
    
        .${base}-enter-active, .${base}In,
        .${base}-leave-active, .${base}Out {
            animation-fill-mode: both;
        }
        
        .${base}-enter-active, .${base}In {
            animation-name: ${base}In;
        }
        
        .${base}-leave-active, .${base}Out {
            animation-name: ${base}Out;
        }
    `;
}

export function getWidgetAnimationIdentifier(animation) {

    return `dmb-animation-custom-${animation.widgetId}-${animation.stage}`
}

export function getAnimationClass(animation) {

    if(animation.type === AnimationType.A_B){
        return getWidgetAnimationIdentifier(animation);
    }
    else {
        return animationTypeClassFilter(animation.type);
    }
}

export function resolveWidgetSize(size: WidgetSizeImplicit | WidgetSizeExplicit, orientation: Orientation): WidgetSizeExplicit {

    if((size as WidgetSizeExplicit).width !== undefined){

        return size as WidgetSizeExplicit;
    }
    else if((size as WidgetSizeImplicit).size !== undefined){

        const implicitSize = size as WidgetSizeImplicit;
        const aspect = 16.0/9.0;

        return {
            width: implicitSize.size,
            height: implicitSize.size * aspect
        }
    }
}